import { useEffect } from 'react';

interface PdfViewerPanelProps {
  /** URL naar de PDF (zelfde origin) */
  url: string;
  /** Toon panel als true */
  open: boolean;
  /** Sluit-callback */
  onClose: () => void;
  /** Optionele titel boven de viewer */
  title?: string;
}

/**
 * Slidende PDF-viewer rechts in beeld voor side-by-side verificatie tijdens matching.
 * Neemt half van de viewport zodat de calculatie-UI links zichtbaar blijft.
 * Op kleinere schermen valt hij terug naar full-screen overlay.
 */
export function PdfViewerPanel({ url, open, onClose, title = 'Opstartfiche' }: PdfViewerPanelProps) {
  // Esc om te sluiten
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop voor mobile / full-screen modus */}
      <div
        className="fixed inset-0 bg-primary/30 z-40 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 h-screen z-50 bg-white shadow-2xl flex flex-col w-full md:w-[50vw] lg:w-[45vw] xl:w-[40vw]"
        role="dialog"
        aria-label={title}
      >
        <div className="flex items-center justify-between bg-primary text-white px-4 py-2">
          <span className="font-bold uppercase tracking-allcaps text-sm">{title}</span>
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noopener"
              className="text-xs px-2 py-1 rounded bg-primary-700 hover:bg-primary-800"
              title="Open in nieuw tabblad"
            >↗ Tab</a>
            <button
              className="text-xl leading-none px-2 hover:text-warning"
              onClick={onClose}
              title="Sluiten (Esc)"
              aria-label="Sluiten"
            >✕</button>
          </div>
        </div>
        <iframe
          src={url + '#toolbar=1&view=FitH'}
          className="flex-1 w-full border-0"
          title={title}
        />
      </aside>
    </>
  );
}
